import type {
    ListItem,
    PathChoice,
    Project,
    Tone
} from '@/types'

/*
 * Every visible line of copy on the site lives here, written exactly as supplied.
 * Edit the words here and every section that uses them follows.
 */

// Photos: set a path here (for example "/images/david-mudry.jpg") and every spot that uses it switches over.
export const photos = {
    headshot: '/images/david-mudry.webp' as string | null
}

export const contact = {
    phone: "(817) 964-8961",
    phoneHref: "tel:+18179648961",
    email: "david@davidmudry.com",
    emailHref: "mailto:david@davidmudry.com",
    linkedin: "https://www.linkedin.com/in/david-mudry-92626013b"
}

export const navLinks = [
    { label: "Work", href: "/#work" },
    { label: "Services", href: "/#services" },
    { label: "About", href: "/#about" }
]

export const quoteLabel = "Get a quote"

export const hero = {
    eyebrow: "Web developer · Fort Worth, TX",
    headline: {
        lead: "Your website,",
        punch: "handled.",
        tail: "Start to finish."
    },
    // Alternate headline, kept here so it can be swapped in: "Fort Worth websites, built right. Built fast."
    alternate: "Fort Worth websites, built right. Built fast.",
    subhead: "I design and build websites for local businesses, launched fast or custom-built to grow. You don’t learn any tools or manage any tech. I handle the design, the build and every detail in between.",
    call: "Call (817) 964-8961",
    rating: "5.0 rating",
    upworkUrl: "https://www.upwork.com/freelancers/davidm669?mp_source=share",
    trust: "Clients who keep coming back"
}

export const clients = {
    label: "Trusted by businesses and agencies",
    names: [
        "J. Bond Insurance",
        "Oakmark Signature Homes",
        "GM Advisory Partners",
        "Mach9",
        "Refuge VFX",
        "Megalodon",
        "Psynth",
        "Omna Institute",
        "Nova Metals"
    ]
}

export type PathOption = {
    id: 'launch' | 'scale'
    tone: Tone
    title: string
    tagline: string
    body: string
    priceLabel: string
    price: string
    cta: string
}

export const paths = {
    heading: {
        first: "Choose",
        second: "your",
        third: "path."
    },
    intro: "Two ways to get a website you’re proud of. Same standards, same developer, different speed and depth.",
    options: [
        {
            id: 'launch',
            tone: 'blaze',
            title: "Launch fast",
            tagline: "A professional site, live in about a week.",
            body: "For businesses that need to look great online now, on a tighter budget. I use the best AI build tools to move fast, then check and finish every detail by hand.",
            priceLabel: "From",
            price: "$1,200",
            cta: "Start with Launch"
        },
        {
            id: 'scale',
            tone: 'cobalt',
            title: "Built to scale",
            tagline: "Custom-designed and hand-built in Webflow.",
            body: "For businesses with a brand to protect, content to grow or a team that edits the site. Built on a real design system, so it stays clean and consistent as you add to it.",
            priceLabel: "From",
            price: "$3,000",
            cta: "Go custom"
        }
    ] as Array<PathOption>,
    comparison: [
        { label: "Best for", launch: "Looking established online, quickly", scale: "Winning more search traffic and growing" },
        { label: "Design", launch: "Proven layout, styled to your brand", scale: "Unique layouts for each main page, with review rounds" },
        { label: "Strategy & copy", launch: "Strategy call and copy help", scale: "Full messaging and page-by-page copy" },
        { label: "SEO", launch: "On-page SEO and local search setup", scale: "Plus service, industry and location pages built to rank" },
        { label: "Timeline", launch: "About 1 week", scale: "2–4 weeks" },
        { label: "Pages", launch: "Up to 5", scale: "As many as you need" },
        { label: "Editing", launch: "Easy editing for text and images", scale: "Easy editing, plus a CMS for new posts, projects and services" }
    ],
    note: {
        before: "Not sure which fits? I’ll recommend the right path on a ",
        link: "free call",
        after: "."
    },
    aiLink: "Already built something with AI? I’ll finish it properly."
}

export const work = {
    heading: "Recent work.",
    intro: "Every site is designed around the client’s brand, not mine.",
    cta: "See all work",
    projects: [
        {
            slug: 'omna-institute',
            name: "Omna Institute",
            description: "From AI prototype to real website. The client designed a concept in Claude. I rebuilt it in Webflow with program pages, pricing, a blog, booking forms, redirects and full testing, ready to launch.",
            image: null,
            tone: 'cobalt'
        },
        {
            slug: 'j-bond-insurance',
            name: "J. Bond Insurance",
            description: "A national bond agency with a custom CMS, so the team manages state-specific bond pages themselves. Integrated quote forms made getting a bond simpler.",
            image: null,
            tone: 'blaze'
        },
        {
            slug: 'refuge-vfx',
            name: "Refuge VFX",
            description: "Their old Squarespace site was triggering security warnings for visitors. I moved them to Webflow, kept the look, and built a CMS for case studies, projects and reels.",
            image: null,
            tone: 'ink'
        },
        {
            slug: 'oakmark-signature-homes',
            name: "Oakmark Signature Homes",
            description: "A premium site for a luxury home builder, with a hero slider, image galleries and a custom contact form, optimized for search and speed.",
            image: null,
            tone: 'paper'
        }
    ] as Array<Project>
}

export const finished = {
    heading: {
        before: "A website that looks",
        hollow: "finished",
        middle: "isn’t always",
        solid: "finished."
    },
    intro: "Anyone can generate a pretty homepage now. What makes a site actually work is the stuff you can’t see, and it’s where cheap and DIY sites fall apart. Every site I build includes:",
    items: [
        { title: "Search-ready SEO", body: "page titles, descriptions, headings, image text and schema, so Google understands your business." },
        { title: "Forms that actually deliver", body: "tested, so leads reach your inbox instead of disappearing." },
        { title: "Mobile done right", body: "checked on real phone and tablet sizes, not just shrunk down." },
        { title: "Fast loading", body: "optimized images and clean code." },
        { title: "Content you can edit", body: "a CMS for blogs, services or projects, with a walkthrough on how to use it." },
        { title: "Analytics", body: "so you know who’s visiting and what’s working." },
        { title: "A clean launch", body: "domain, redirects, Search Console and a final round of testing." }
    ] as Array<ListItem>,
    closing: "You don’t need to know what any of that means. That’s my job."
}

export const testimonials = {
    featured: {
        quote: "I initially tried building the site myself using a Webflow template. While it looked decent, the functionality just wasn’t there. David stepped in and completely transformed it… He patiently answered all of my questions, guided me through the process, and delivered a website that I’m truly confident sharing with customers.",
        author: "Nova Metals"
    },
    row: [
        { quote: "As someone with no prior website experience, I really appreciated how clearly he explained everything.", author: "" },
        { quote: "He brought our website visions to life and we will continue to use him for any future changes.", author: "J. Bond Insurance Agency" },
        { quote: "Thoughtfully understands your objectives and delivers more than expected.", author: "GM Advisory Partners" },
        { quote: "Stellar experience working with David. He knows his stuff and does it well!", author: "Astoria Advertising" },
        { quote: "I had little to no edits to give him because he did everything exceptionally.", author: "Moxie Made" },
        { quote: "Very responsive, actively engaged, thoughtful, and helpful.", author: "Megalodon Group" }
    ]
}

export const howItWorks = {
    heading: "How it works.",
    intro: "Same four steps on either path. You always know what’s happening and what’s next.",
    steps: [
        { title: "Discover.", body: "A free call about your business, your customers and what the site needs to do. I’ll recommend the right path and give you a fixed price." },
        { title: "Design.", body: "You see the direction early and tell me what you think. Launch moves fast here; Custom gets a full design you approve before the build." },
        { title: "Build.", body: "I build the site and handle the details: SEO, forms, mobile, speed and content setup." },
        { title: "Launch.", body: "We test everything, go live, and I show you how to make updates yourself. Or I keep handling them for you." }
    ] as Array<ListItem>,
    timeline: "Launch takes about 1 week. Custom takes 2–8 weeks, depending on size.",
    price: "The price is the price. If budget is tight, we adjust the scope, not the rate."
}

export const existingSite = {
    heading: "Already have a website?",
    intro: "You don’t need to start over to work with me.",
    items: [
        { title: "Fixes and improvements", body: "broken layouts, mobile issues, slow pages, forms that don’t send." },
        { title: "New pages", body: "added to match your existing design." },
        { title: "Switching platforms", body: "move from Squarespace, Wix or WordPress without losing your Google rankings." },
        { title: "Finishing AI-built sites", body: "started something in an AI tool? I’ll make it a real, working website." },
        { title: "Ongoing support", body: "a monthly care plan, or help whenever you need it. Most of my clients keep coming back." }
    ] as Array<ListItem>,
    cta: "Tell me what you need"
}

export const about = {
    heading: {
        greeting: "Hi,",
        name: "I’m David."
    },
    intro: "I’m a web developer in Fort Worth, and you work directly with me: no salespeople, no handoffs, no account managers. I got into web design by accident while building a video game, and I’ve been hooked ever since.",
    champion: {
        lead: "One more thing.",
        claimLead: "I’m the",
        claim: "2023 Season 83 PewDiePie Tuber Simulator Video Game World Champion.",
        shout: "That’s right,",
        title: "WORLD CHAMPION.",
        story: "It’s not every day someone reaches the absolute pinnacle of mobile gaming glory, but somehow, some way… I did. Is it relevant to web design? No, not really. But does it demonstrate greatness, dedication, and an unshakable competitive spirit? Absolutely."
    },
    cta: "More about me"
}

export const faqs = [
    {
        question: "How much does a website cost?",
        answer: "Launch sites start at $1,200. Custom Webflow sites start at $3,000, and most land between $3,000 and $6,000. You get a fixed price before anything starts. If the budget is tight, we adjust the scope, not the rate."
    },
    {
        question: "Which path is right for me?",
        answer: "If you need a professional site quickly and want to keep costs down, Launch is a great fit. If you need a custom design, lots of content or a site your team will grow into, go Custom. Not sure? Book a free call and I’ll recommend one."
    },
    {
        question: "Is an AI-built site lower quality?",
        answer: "Not the way I do it. AI speeds up the build, but I check and finish every detail by hand: SEO, forms, mobile, speed and launch. The difference between my Launch sites and cheap AI sites is everything you can’t see."
    },
    {
        question: "Do I own my website?",
        answer: "Yes. Your domain, content and accounts are set up in your name, and you can always move to another developer."
    },
    {
        question: "Can I switch platforms without losing my Google rankings?",
        answer: "Yes. I map your existing pages, set up redirects and carry over your SEO settings, so search engines follow you to the new site."
    },
    {
        question: "Can I update the site myself?",
        answer: "Yes. I’ll show you how to make everyday changes. If you’d rather not, I offer monthly care plans, or ad-hoc help at $60 an hour."
    }
]

export const pathChoices: Array<{ id: PathChoice, label: string }> = [
    { id: 'launch', label: "Launch fast" },
    { id: 'scale', label: "Built to scale" },
    { id: 'unsure', label: "Not sure" },
    { id: 'fix', label: "Fix or finish my current site" }
]

export const contactSection = {
    heading: "Let’s build your website.",
    intro: "Tell me a bit about your business and what you need. I usually reply within one business day. Based in Fort Worth, working with businesses anywhere.",
    fields: {
        name: "Name",
        email: "Email",
        phone: "Phone",
        path: "Which path?",
        message: "Message"
    },
    submit: "Send message",
    talk: "Prefer to talk?",
    call: "Call (817) 964-8961"
}

export type DesignFit = 'match' | 'refine' | 'finish' | 'unsure'

export type StartingPoint = {
    id: Exclude<DesignFit, 'unsure'>
    name: string
    headline: string
    text: string
    bestFor: string
}

// The "AI Design to Website" page (/ai-design-to-website).
export const aiDesign = {
    seo: {
        title: "Turn Your AI Website Design Into a Real Website | Mudry Design",
        description: "Designed a website with Claude, ChatGPT or another AI tool? Mudry Design builds it for real: fast, editable, SEO-ready and fully yours."
    },
    hero: {
        eyebrow: "AI design to website",
        headline: {
            lead: "Designed your website with AI?",
            punchLead: "I’ll build it",
            punch: "for real."
        },
        subhead: "Whether it’s polished, rough or half-finished, I’ll turn your AI design into a fast, working website you own and can edit yourself.",
        primary: "Send me your design",
        secondary: "Get a free review",
        trust: {
            rating: "5.0 rating on",
            upwork: "Upwork",
            location: "Based in Fort Worth, TX",
            reach: "Working with clients anywhere"
        }
    },
    tools: {
        label: "Works with designs from any AI tool",
        names: ["Claude", "ChatGPT", "Lovable", "Bolt", "v0", "Figma Make", "Framer"],
        more: "and more"
    },
    startingPoints: {
        heading: "Wherever your design is, I’ll take it from there.",
        intro: "You’ve done the hard part: you know what you want your website to look like. Here’s how I help, depending on where you are.",
        bestForLabel: "Best for",
        options: [
            {
                id: 'match',
                name: "Match it",
                headline: "Love your design? I’ll build it exactly as you see it.",
                text: "Every color, font and layout carried over faithfully, now with real hosting, easy editing, SEO and forms that actually work.",
                bestFor: "Designs you’re happy with"
            },
            {
                id: 'refine',
                name: "Refine it",
                headline: "Close, but something’s off? I’ll fix what isn’t working.",
                text: "Spacing, mobile layouts, readability and flow: the details that separate a nice-looking draft from a professional website.",
                bestFor: "Designs that feel almost right"
            },
            {
                id: 'finish',
                name: "Finish it",
                headline: "Ran out of road? I’ll pick up where the AI left off.",
                text: "Missing pages, unfinished sections, a direction that needs completing, all designed to match your vision.",
                bestFor: "Designs that are partly done"
            }
        ] as Array<StartingPoint>
    },
    whyNot: {
        heading: "Why not just launch the AI version?",
        intro: "An AI preview can look finished and still be missing most of what makes a website work. Here’s what I add to every project.",
        items: [
            { title: "Real hosting and your own domain", body: "Your site lives at yourbusiness.com on fast, reliable hosting." },
            { title: "Easy editing", body: "Change text and images yourself in a few clicks. No code or AI prompts needed." },
            { title: "SEO setup", body: "Page titles, descriptions, headings and structure, so Google understands your business." },
            { title: "Forms that deliver", body: "Tested end to end, so leads land in your inbox instead of disappearing." },
            { title: "Mobile done right", body: "Checked on real phone and tablet sizes, not just shrunk down." },
            { title: "Fast loading", body: "Optimized images and clean code, so visitors don’t leave before the page loads." },
            { title: "Analytics", body: "See who’s visiting and what’s working." },
            { title: "A clean launch", body: "Domain setup, redirects, Search Console and a final round of testing." }
        ] as Array<ListItem>,
        closing: "You don’t need to know what any of that means. That’s my job."
    },
    howItWorks: {
        heading: "How it works.",
        steps: [
            { title: "Send your design.", body: "A share link, screenshots, files or code. Whatever you have works." },
            { title: "Get a fixed quote.", body: "I review it, tell you exactly what it needs and give you one price. No surprises." },
            { title: "I build it.", body: "Matched, refined or finished, with all the behind-the-scenes work handled." },
            { title: "Launch.", body: "We go live, and I show you how to make updates yourself. Or I keep handling them for you." }
        ] as Array<ListItem>
    },
    whatToSend: {
        heading: "Not sure what to send? Anything works.",
        items: [
            "A share link from your AI tool",
            "Screenshots of each page",
            "Exported files or code",
            "A quick description of what’s missing"
        ],
        note: "Not sure your design is ready? Send it anyway. Half-finished is exactly what I’m here for."
    },
    caseStudy: {
        eyebrow: "Case study",
        heading: "Momentum Wealth: from AI concept to launch-ready website",
        text: "Momentum Wealth, a fiduciary financial advisory firm in Columbus, Georgia, started with a website concept designed in AI and needed it turned into something real. I built it in Webflow with pages for each of their five services, a home for their weekly podcast and essays, appointment booking and the disclosures a financial firm needs, then tested everything before launch.",
        highlights: [
            "Designed in AI",
            "Built in Webflow",
            "Five service pages",
            "Podcast and essay hubs",
            "Booking, disclosures and full testing"
        ],
        // Before/after comparison. Set both paths to switch on the slider; while either is null a marked image slot shows instead.
        image: {
            label: "Before/after: AI concept next to the live site",
            before: "/images/momentum-wealth-before.webp" as string | null,
            beforeAlt: "Momentum Wealth website concept designed in AI",
            after: "/images/momentum-wealth-after.webp" as string | null,
            afterAlt: "Momentum Wealth live website built in Webflow"
        }
    },
    platform: {
        heading: "Built on the right platform for you.",
        options: [
            { title: "Webflow", body: "For sites that will grow. A scalable CMS for blogs, projects and services, ideal when a team will edit the site or you’ll add content often." },
            { title: "Fast-launch platform", body: "For sites that need to go live quickly, with easy editing built in. A great fit for focused sites of up to five pages." }
        ] as Array<ListItem>,
        note: {
            before: "Either way, you own your site. Not sure which fits? I’ll recommend one in your ",
            link: "free review",
            after: "."
        }
    },
    freeReview: {
        heading: "Get a free review of your AI design.",
        text: "Send me your design link and I’ll record a short video walking through exactly what it needs to go live: what’s working, what to fix and what’s missing. No cost, no obligation.",
        cta: "Get my free review"
    },
    faqHeading: "Frequently asked questions",
    faqs: [
        {
            question: "Will it look the same as my design?",
            answer: "If you want it to, yes. With Match it, I carry your design over faithfully. If something won’t work well on a real website, like a layout that breaks on phones, I’ll flag it and suggest a fix before changing anything."
        },
        {
            question: "Can you use the code my AI tool generated?",
            answer: "Usually I rebuild it properly instead. AI-generated code is often hard to edit, slow, or missing the structure search engines need. Rebuilding is typically faster and gives you a cleaner site that’s easier to maintain."
        },
        {
            question: "Can you improve my design while you build it?",
            answer: "Yes. That’s what Refine it is for. Tell me what’s bothering you, or I’ll point out what I’d improve in your free review."
        },
        {
            question: "What if my design is only partly finished?",
            answer: "That’s common, and it’s what Finish it is for. I’ll design the missing pages and sections to match what you’ve already created."
        },
        {
            question: "Which AI tools do you work with?",
            answer: "Any of them: Claude, ChatGPT, Lovable, Bolt, v0, Figma Make, Framer and others. If you can share a link, screenshots or files, I can work with it."
        },
        {
            question: "Do I own the finished website?",
            answer: "Yes. Your domain, content and accounts are set up in your name, and you can move to another developer anytime."
        },
        {
            question: "Can I edit the site myself after launch?",
            answer: "Yes. I’ll show you how to update text and images yourself. If you’d rather not, I offer monthly care plans."
        },
        {
            question: "How long does it take?",
            answer: "Most projects take one to four weeks, depending on size and how much refining or finishing your design needs."
        },
        {
            question: "How much does it cost?",
            answer: "Every project gets a fixed quote after I review your design. Most start around $2,000, depending on page count and features."
        },
        {
            question: "Do you work with clients outside Fort Worth?",
            answer: "Yes. I’m based in Fort Worth, Texas, and work with clients anywhere. If you’re in DFW, we can meet in person."
        }
    ],
    contact: {
        heading: "Send me your design.",
        intro: "Tell me a bit about your business and share whatever you have. I’ll tell you exactly what it needs, usually within one business day.",
        fields: {
            name: "Name",
            email: "Email",
            phone: "Phone",
            link: "Link to your design",
            upload: "or upload files",
            fit: "Which fits best?",
            message: "Message"
        },
        fitChoices: [
            { id: 'match', label: "Match it" },
            { id: 'refine', label: "Refine it" },
            { id: 'finish', label: "Finish it" },
            { id: 'unsure', label: "Not sure" }
        ] as Array<{ id: DesignFit, label: string }>,
        submit: "Send my design",
        talk: "Prefer to talk?",
        call: "Call (817) 964-8961"
    }
}

export const footer = {
    tagline: "Web developer in Fort Worth, Texas",
    links: [
        { label: "Work", href: "/#work" },
        { label: "Services", href: "/#services" },
        { label: "AI Design to Website", href: "/ai-design-to-website" },
        { label: "About", href: "/#about" },
        { label: "Contact", href: "/#contact" }
    ],
    linkedin: "LinkedIn",
    copyright: "© 2026 David Mudry",
    privacy: "Privacy Policy"
}
